/**
 * The map model every web consumer reads (`SchemaData`) — the minimal
 * fields the web client uses. Built from the bundled London artifact
 * (`public/map-v2/schema.json`, contract in `./v2/types`) by
 * `scripts/mapSchemaV2.mjs`; the shape predates v2, which is why its
 * docs mention v1 schema versions. Mobile model parity:
 *   - iOS: `metro-ios/MetroCore/Sources/Core/Models/`
 *   - Android: `metro-android/core/data/src/main/java/.../model/`
 *
 * Extra properties not declared here are kept at runtime — TypeScript only
 * narrows what we read.
 *
 * Schema version support: renderer targets v1.21 geometry; v1.23 adds
 * `slug` and `primaryLineId` for deep-link URLs (see
 * `wiki/contracts/schema-v1-23-slug-primary-line.md`).
 */

/**
 * Per-theme SVG path overrides introduced in schema v1.21. When the active
 * theme is missing, callers fall back to the element's base `svgPath`
 * (mirrors iOS `_Drawable.svgPath(for: interfaceStyle)`).
 */
export interface SchemaModesSvg {
  light?: string
  dark?: string
  /**
   * The element's markup in the late-night frame (TASK-1136, v2 `paths.night`),
   * dark palette; absent when the frame does not draw it or the city has none.
   */
  night?: string
}

/**
 * Line transport category surfaced by the backend since v1.21. Drives the
 * `RenderFloor` painter's-algorithm layer assignment for each connection.
 * Mirrors iOS `Line.LineType` (`MetroCore/Sources/Core/Models/`). Unknown /
 * missing values should fall back to `CONNECTION_DEFAULT` at the call site.
 */
export type LineType =
  | 'UNDERGROUND'
  | 'TRAM'
  | 'CABLE_CAR'
  | 'ELIZABETH_LINE'
  | 'DLR'
  | 'OVERGROUND'
  | 'NATIONAL_RAIL'

/**
 * Minimum shape of an inline `line` block — the fields the renderer + the
 * station-panel URL wiring need. Actual schema also carries
 * `shortLocalName`, `modes`, etc.; those are ignored here until we need
 * them on the panel.
 */
export interface SchemaLine {
  id: string
  /** v1.23+ — URL-safe line identifier (e.g. `piccadilly`, `hammersmith-city`). Used in `?line=<slug>` query param and `/lines/<slug>` routes. */
  slug?: string
  /** v1.23+ — human-readable line name used in panel header ("Piccadilly line"). */
  localName?: string
  /** Badge code ("CEN", "ELIZ", "TRAM"); route badges prefer it over a 3-letter cut of `localName`. */
  shortLocalName?: string
  type: LineType
  hexColor: string
  /**
   * Per-theme line palette — the SAME palette the per-theme MMTG map
   * geometry is baked from. UI chrome (pills, badges, rails, suggester
   * dots) MUST select `colorModes[theme]` so it matches the map in both
   * themes; the flat `hexColor` is the fallback for schemas that
   * predate the field. See `lineHexForTheme` (2026-07 color contract:
   * pill == map, drift is a critical bug).
   */
  colorModes?: {
    light?: string
    dark?: string
  }
}

export interface SchemaConnection {
  id: string
  lineId: string
  svgPath: string
  modesSvg?: SchemaModesSvg
  /**
   * Travel time in seconds along this connection. Required by Routing;
   * optional here only because legacy fixtures may omit it. The
   * `routingAdapter` skips connections where this — or
   * `fromStationId` / `toStationId` — are missing and emits a single
   * dev-time warn per build.
   */
  weight?: number
  /**
   * Source station id (per-line cluster record id, e.g. `central.940GZZLUOXC`).
   * Required by Routing adapter; optional here for forward
   * compatibility with stripped-down fixtures.
   */
  fromStationId?: string
  /** Destination station id. Required by Routing adapter. */
  toStationId?: string
  /**
   * When `true`, the routing algorithm considers travel in both
   * directions on this connection. Adapter assumes `false` if absent.
   */
  reversible?: boolean
  /**
   * Night Tube visibility. Production schema emits this on every connection;
   * optional here for legacy/stripped fixtures. See `elementVisibleInMode`.
   * Mirrors iOS `Connection.modes`.
   */
  modes?: readonly Mode[]
  /**
   * Platform sign when travelling `fromStationId` → `toStationId`
   * ("Westbound", …), schema ≥ 1.30.0. Shown verbatim in every locale.
   * See `platformBoundLookup` in `@metro/route-details`.
   */
  forwardBound?: string
  /** Platform sign for the opposite direction (reversible connections). */
  reverseBound?: string
}

export interface SchemaStation {
  id: string
  /** TfL identifier shared across cluster siblings (e.g. Stratford's
   *  `central.940GZZLUSTD` and `jubilee.940GZZLUSTD` share one
   *  `naptanId`). Used by the routing algorithm to detect
   *  cluster-revisits across distinct per-line records. */
  naptanId?: string
  /** v1.23+ — URL-safe station identifier shared across all records in a station cluster (see schema-v1-23 contract). Route path component for `/stations/<slug>`. */
  slug?: string
  /** v1.23+ — cluster default line ID (lex-first when multiple lines serve the station), used when URL has no `?line=` query param. */
  primaryLineId?: string
  localName: string
  x: number
  y: number
  /**
   * Geographic coordinate of the station — carried by the map's stations
   * (`stations[].coordinate` in the v2 artifact).
   * Optional here only so stripped-down test fixtures need not supply it,
   * mirroring the other v1.23+ fields. Consumed by the StationDetails
   * panel and the `Location` nearest-station provider (TASK-391).
   */
  coordinate?: { readonly latitude: number; readonly longitude: number }
  svgPath?: string
  modesSvg?: SchemaModesSvg
  /**
   * The marker centre in night mode where the late-night frame moves it
   * (TASK-1136, v2 `stops[].night`); pins and bubbles use `night ?? {x, y}`.
   */
  night?: { x: number; y: number }
  line: SchemaLine
  /** v1.23+ fields present in schema.json but not wired into render pipeline yet — panel reads these directly. */
  zone?: string | null
  facilities?: readonly string[]
  transferStationsIds?: readonly string[]
  /**
   * Per-direction first/last train times (v1.21+). Times are `HH:MM`
   * strings; schema v1.29 carries `--:--` literals where TfL data is
   * missing (TASK-646) — consumers must sanitise, see
   * `openTodayRange`. Optional: stripped fixtures and a few stations
   * omit it.
   */
  stationTimetable?: SchemaStationTimetable
  /**
   * Night Tube visibility. Production schema emits this on every station;
   * optional here for legacy/stripped fixtures. Mirrors iOS `Station.modes`.
   */
  modes?: readonly Mode[]
}

/** One edge of a service day — `HH:MM`, or the `--:--` placeholder. */
export interface SchemaTimetableSpan {
  firstTrain?: string
  lastTrain?: string
}

/** One direction of a station's timetable (v1.21+). */
export interface SchemaTimetableDirection {
  destinationStationLocalName?: string
  weekdays?: SchemaTimetableSpan
  saturdays?: SchemaTimetableSpan
  sundays?: SchemaTimetableSpan
}

/** `SchemaStation.stationTimetable` payload. */
export interface SchemaStationTimetable {
  directions: readonly SchemaTimetableDirection[]
}

/**
 * Night Tube / normal mode. Matches `metro-server/backend/base/Mode.go` and
 * iOS `MetroCore/Sources/Core/Models/Mode.swift` (`case night/normal/unknown`).
 * `'unknown'` mirrors iOS `Mode(rawValue:) ?? .unknown` — graceful fallback
 * when the schema emits a value future clients do not recognise. Hit-tester,
 * filter, and state layer use this to decide which elements are active.
 */
export type Mode = 'normal' | 'night' | 'unknown'

/**
 * Decode a raw schema string into a `Mode`, falling back to `'unknown'`.
 * Mirrors iOS `Mode(_ rawValue: String)` constructor.
 */
export function decodeMode(raw: string | undefined | null): Mode {
  if (raw === 'normal' || raw === 'night' || raw === 'unknown') return raw
  return 'unknown'
}

/**
 * V1-parity visibility check for a mode-aware element. `modes === undefined`
 * returns `true` (fixtures predating the contract — production schema emits
 * `modes` on every element). Mirrors iOS `modes.contains(mode)` predicate
 * used in `MapData.filtered(by:)`.
 */
export function elementVisibleInMode(modes: readonly Mode[] | undefined, mode: Mode): boolean {
  if (modes === undefined) return true
  return modes.includes(mode)
}

/**
 * Tappable rectangle in canvas coordinates. Mirrors
 * `metro-server/backend/tapgrids/Tapgrid.go`. The iteration order of
 * `SchemaData.tapGrids` is authoritative — first-match wins on overlap.
 */
export interface SchemaTapGrid {
  id: string
  size: { width: number; height: number }
  origin: { x: number; y: number }
  stationId: string
  modes: readonly Mode[]
  /** The tap area in night mode where the late-night frame moves it (TASK-1136, v2 `stops[].night.tapGrid`). */
  night?: { size: { width: number; height: number }; origin: { x: number; y: number } }
}

export interface SchemaAdditional {
  id: string
  svgPath: string
  modesSvg?: SchemaModesSvg
  /** Night Tube visibility. Optional for legacy fixtures. Mirrors iOS `Additional.modes`. */
  modes?: readonly Mode[]
  /**
   * Direction arrows only: the one-way connection the arrow sits on. The
   * MMTG draws it inside that connection; the route highlight overlay
   * draws it after the highlighted connections.
   */
  connectionId?: string
}

/**
 * Transition between two stations (transfer tunnel). Drawn on top of
 * stations as a rounded-rect with a subtle stroke, representing the
 * footprint of a station-cluster transfer. Colors are **pre-baked** per
 * theme variant in `modesSvg`:
 *   - `light`: fill `#FFFFFF`, stroke `#D9D9D9`
 *   - `dark`:  fill `#1C1C1C`, stroke `#A7A7A7`
 *
 * The route overlay draws `modesSvg.{light,dark}` directly; the map draws
 * the pre-tessellated MMTG transitions.
 *
 * Source: the v2 artifact's `transfers[]` (`scripts/mapSchemaV2.mjs`),
 * mobile parity in `metro-ios/MetroCore/.../Transition.swift` and
 * `metro-android/core/data/.../model/Transition.kt`.
 */
export interface SchemaTransition {
  id: string
  fromStationId: string
  toStationId: string
  /** Base SVG (legacy); per-theme overrides live in `modesSvg`. */
  svgPath?: string
  modesSvg?: SchemaModesSvg
  /**
   * When `true`, the transition should be skipped entirely at cold-start
   * render time — a legacy iOS optimisation for transitions whose outer
   * geometry is already covered by an adjacent station glyph, so drawing
   * them burns memory + GPU time without a visible change.
   *
   * v1.21 schema has 171 elements (~152 of them transitions) with this
   * flag set. Cross-platform: iOS `Transition.skipInitialDraw: Bool`,
   * Android `Transition.kt:skipInitialDraw: Boolean`. The web client
   * must honour it in `buildTransitionGeometries` for parity.
   */
  skipInitialDraw?: boolean
  /**
   * Walk-transfer time in seconds. Required by Routing; optional here
   * only because legacy fixtures may omit it. The `routingAdapter`
   * skips transitions where this — or the line lookup for either
   * endpoint — fails, and emits a single dev-time warn per build.
   */
  weight?: number
  /**
   * `true` for a STREET walk link between two different physical
   * stations outside the gate line (schema 1.27.0+, e.g.
   * `transition-bak-ERB-cir-ERC0` — Edgware Road Bakerloo ↔ Circle).
   * Offline routing builds a routable walk edge from it and emits the
   * leg in the 2026-07 street-walk wire shape (EMPTY transition_id);
   * in-station transitions leave the flag unset.
   */
  street?: boolean
  /**
   * When `true`, the routing algorithm considers walking the transfer
   * in both directions. Adapter assumes `false` if absent.
   */
  reversible?: boolean
  /**
   * Per-approach convenient-transfer car recommendations (schema v1.23+).
   * Each entry says: a rider who alighted at `toStationId` having come
   * FROM `prevStationId` on the incoming line should ride in the `types`
   * cars to minimise the transfer walk. `types` use the SCREAMING_SNAKE
   * wire tokens (FIRST / NEAR_FIRST / CENTER / NEAR_END / END / ALL).
   *
   * Cross-platform: this is the SAME source the backend uses to produce
   * the iOS/Android `convenient_cars` leg field — web (which routes
   * locally) derives the equivalent set from these entries so the
   * RouteDetails S4/S5 possibleTransfer is contract-identical across
   * platforms. See `buildPossibleTransfersByLeg`.
   */
  convenientTransferCars?: readonly SchemaConvenientTransferCars[]
  /** Night Tube visibility. Optional for legacy fixtures. Mirrors iOS `Transition.modes`. */
  modes?: readonly Mode[]
}

/**
 * One approach-keyed convenient-car recommendation inside
 * {@link SchemaTransition.convenientTransferCars}.
 */
export interface SchemaConvenientTransferCars {
  /** Station the rider arrived from on the incoming line (the stop
   *  before the alighting station). */
  readonly prevStationId: string
  /** Station the rider alighted at to make the transfer. */
  readonly toStationId: string
  /** SCREAMING_SNAKE car tokens (FIRST / NEAR_FIRST / … / ALL). */
  readonly types: readonly string[]
}

/**
 * Pre-rendered station name label (outlined glyph paths). Schema v1.22+
 * carries 722 of these. The renderer draws every text with
 * `skipInitialDraw === false` on the topmost map layer (`RenderFloor.TEXT`).
 *
 * `skipInitialDraw === true` texts are NOT drawn on the base map. They are
 * reserved for a future route-rendering feature that will draw highlighted
 * station names along a selected route. Cross-platform parity:
 * iOS `DrawableText`, Android `DrawableText`.
 */
export interface SchemaText {
  id: string
  svgPath: string
  modesSvg?: SchemaModesSvg
  skipInitialDraw: boolean
  stationId?: string
  /** Night Tube visibility. Optional for legacy fixtures. Mirrors iOS `DrawableText.modes`. */
  modes?: readonly Mode[]
}

export interface SchemaData {
  version: string
  stations: readonly SchemaStation[]
  connections: readonly SchemaConnection[]
  additional: readonly SchemaAdditional[]
  /** v1.21+ — 344 transitions in current schema. Optional for test fixtures. */
  transitions?: readonly SchemaTransition[]
  /** v1.22+ — 722 texts in current schema. Optional for test fixtures. */
  texts?: readonly SchemaText[]
  /**
   * v1.21+ tappable rectangles per station. Optional for test fixtures
   * that don't need interaction coverage.
   */
  tapGrids?: readonly SchemaTapGrid[]
  /**
   * TASK-986 — a v1 slug no v2 station serves → its station's v2 slug
   * (`legacy-ids.json` `slugs`, loaded with the schema by `fetchSchema`).
   * `resolveStation` reads a slug through it. Absent in fixtures.
   */
  retiredSlugs?: Readonly<Record<string, string>>
}
